import React from 'react'
import { useDonation } from '../contexts/DonationContext'
import { Flame, Target, TrendingUp, CheckCircle } from 'lucide-react'

// ============================================================================
// CAMPAIGN MODE TOGGLE
// 'active' = Active campaign with specific goal (e.g., Thanksgiving 2025)
// 'future' = Between campaigns - shows final results from last campaign
// ============================================================================
const CAMPAIGN_MODE: 'active' | 'future' = 'future'

// ============================================================================
// THANKSGIVING 2025 FINAL RESULTS (Frozen)
// These are the final numbers from the completed Thanksgiving 2025 campaign
// Update these values when a campaign concludes to preserve the final stats
// ============================================================================
const THANKSGIVING_2025_FINAL = {
  total_raised: 386.40,      // Final amount raised
  total_tails_funded: 46,    // Final tails funded
  goal_amount: 2016.00,      // Original goal
  goal_tails: 240,           // Original tail goal
  percent_complete: 19.2,    // Final percentage
}

/**
 * DONATION PROGRESS BAR
 *
 * Displays real-time campaign progress with visual progress bar,
 * showing total raised, tails funded, and percentage of goal achieved.
 * Supports "Overtime Mode" with stretch goals when initial goal is reached.
 * In 'future' mode, shows frozen final results from the completed campaign.
 */
const ProgressBar: React.FC = () => {
  const { progress } = useDonation()

  // In future mode, use frozen Thanksgiving 2025 results
  // In active mode, use live progress from the database
  const isFutureMode = CAMPAIGN_MODE === 'future'

  if (!progress && !isFutureMode) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 mb-8 animate-pulse">
        <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded mb-4 w-3/4"></div>
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded mb-6"></div>
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
      </div>
    )
  }

  // Use frozen results in future mode, live results in active mode
  const total_raised = isFutureMode ? THANKSGIVING_2025_FINAL.total_raised : progress?.total_raised ?? 0
  const total_tails_funded = isFutureMode ? THANKSGIVING_2025_FINAL.total_tails_funded : progress?.total_tails_funded ?? 0
  const goal_amount = isFutureMode ? THANKSGIVING_2025_FINAL.goal_amount : progress?.goal_amount ?? 0
  const goal_tails = isFutureMode ? THANKSGIVING_2025_FINAL.goal_tails : progress?.goal_tails ?? 0
  const percent_complete = isFutureMode ? THANKSGIVING_2025_FINAL.percent_complete : progress?.percent_complete ?? 0
  
  // These are only used in active mode
  const tails_remaining = progress?.tails_remaining ?? 0
  const is_overtime = isFutureMode ? false : progress?.is_overtime ?? false
  const overtime_tails = progress?.overtime_tails ?? 0
  const stretch_goal_tails = progress?.stretch_goal_tails ?? 0
  const stretch_goal_amount = progress?.stretch_goal_amount ?? 0
  const stretch_percent_complete = progress?.stretch_percent_complete ?? 0

  // Calculate progress percentage (capped at 100% for initial goal)
  const progressPercent = Math.min(percent_complete, 100)

  // For overtime mode, show progress toward stretch goal
  const displayPercent = is_overtime ? stretch_percent_complete : progressPercent

  return (
    <div className={`rounded-2xl shadow-2xl p-8 mb-12 ${
      is_overtime 
        ? 'bg-gradient-to-br from-[#FF6B35] via-[#8B3A3A] to-[#6B2A2A]' 
        : 'bg-gradient-to-br from-[#8B3A3A] to-[#6B2A2A]'
    }`}>
      {/* Overtime Badge */}
      {is_overtime && (
        <div className="flex justify-center mb-4">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-500 to-red-500 text-white px-6 py-2 rounded-full shadow-lg animate-pulse">
            <Flame className="w-5 h-5" />
            <span className="font-bold text-lg">OVERTIME MODE</span>
            <Flame className="w-5 h-5" />
          </div>
        </div>
      )}

      {/* Header */}
      <div className="text-center mb-6">
        <h2 className="text-3xl md:text-4xl font-bold mb-2 text-white">
          {CAMPAIGN_MODE === 'future' 
            ? 'Thanksgiving 2025 Results'
            : (is_overtime ? 'Stretch Goal Progress' : 'Campaign Progress')}
        </h2>
        <p className="!text-white text-lg">
          {CAMPAIGN_MODE === 'future'
            ? 'Final results from our Thanksgiving campaign'
            : (is_overtime
              ? `🔥 Goal smashed! Now reaching for ${stretch_goal_tails} tails!`
              : `Help us reach our goal of $${goal_amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`)}
        </p>
      </div>

      {/* Amount Raised */}
      <div className="text-center mb-6">
        <div className="text-6xl md:text-7xl font-bold mb-2 text-white">
          ${total_raised.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </div>
        <div className="text-xl !text-white">
          {CAMPAIGN_MODE === 'future'
            ? `raised for Thanksgiving 2025`
            : (is_overtime 
              ? `raised toward $${stretch_goal_amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} stretch goal`
              : `raised of $${goal_amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} goal`
            )
          }
        </div>
      </div>

      {/* Progress Bar */}
      <div className="relative w-full h-8 bg-white/20 rounded-full overflow-hidden mb-2 shadow-inner">
        <div
          className={`absolute top-0 left-0 h-full transition-all duration-1000 ease-out shadow-lg ${
            is_overtime 
              ? 'bg-gradient-to-r from-orange-400 to-yellow-400' 
              : 'bg-gradient-to-r from-[#DAA520] to-[#FFD700]'
          }`}
          style={{ width: `${displayPercent}%` }}
        >
          <div className="absolute inset-0 bg-white/20 animate-pulse"></div>
        </div>
        <div className="absolute inset-0 flex items-center justify-center text-sm font-bold text-white drop-shadow-lg">
          {displayPercent.toFixed(1)}%
        </div>
      </div>

      {/* Stretch Goal Markers (only in overtime) */}
      {is_overtime && (
        <div className="flex justify-between text-xs text-white/70 mb-6 px-1">
          <span>Previous: {goal_tails} tails</span>
          <span className="flex items-center gap-1">
            <Target className="w-3 h-3" />
            Next: {stretch_goal_tails} tails
          </span>
        </div>
      )}

      {/* Stats Grid */}
      <div className={`grid gap-6 text-center ${CAMPAIGN_MODE === 'future' ? 'grid-cols-1 md:grid-cols-3' : (is_overtime ? 'grid-cols-1 md:grid-cols-4' : 'grid-cols-1 md:grid-cols-3')}`}>
        {/* Tails Funded */}
        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
          <div className="text-4xl font-bold text-white mb-1">
            {total_tails_funded}
          </div>
          <div className="text-sm !text-white">
            {CAMPAIGN_MODE === 'future' ? 'Tails Were Funded' : `Lobster ${total_tails_funded === 1 ? 'Tail' : 'Tails'} Funded`}
          </div>
        </div>

        {/* Overtime Bonus Tails (only in overtime and active mode) */}
        {is_overtime && CAMPAIGN_MODE === 'active' && (
          <div className="bg-orange-500/30 backdrop-blur-sm rounded-xl p-4 border border-orange-400/50">
            <div className="text-4xl font-bold text-orange-300 mb-1 flex items-center justify-center gap-1">
              <TrendingUp className="w-6 h-6" />
              +{overtime_tails}
            </div>
            <div className="text-sm text-orange-200">Bonus {overtime_tails === 1 ? 'Tail' : 'Tails'}!</div>
          </div>
        )}

        {/* Tails to Next Goal / Remaining (hide in future mode) */}
        {CAMPAIGN_MODE === 'active' && (
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
            <div className="text-4xl font-bold text-white mb-1">
              {is_overtime ? stretch_goal_tails - total_tails_funded : tails_remaining}
            </div>
            <div className="text-sm text-white">
              {is_overtime ? 'To Next Milestone' : (tails_remaining === 0 ? 'All Funded!' : `${tails_remaining === 1 ? 'Tail' : 'Tails'} Remaining`)}
            </div>
          </div>
        )}

        {/* People Fed */}
        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
          <div className="text-4xl font-bold text-white mb-1">
            {total_tails_funded}
          </div>
          <div className="text-sm !text-white">
            {CAMPAIGN_MODE === 'future' ? 'People Were Fed' : 'People Fed'}
          </div>
        </div>

        {/* Goal Achieved Badge (only in future mode) */}
        {CAMPAIGN_MODE === 'future' && (
          <div className="bg-gradient-to-br from-[#DAA520] to-[#B8860B] backdrop-blur-sm rounded-xl p-4 border-2 border-white/30 shadow-lg">
            <div className="flex items-center justify-center mb-2">
              <div className="bg-white rounded-full p-2">
                <CheckCircle className="w-6 h-6 text-[#DAA520]" />
              </div>
            </div>
            <div className="text-sm font-bold !text-white">Campaign Complete</div>
          </div>
        )}
      </div>

      {/* Overtime Celebration Message (only in active mode) */}
      {is_overtime && CAMPAIGN_MODE === 'active' && (
        <div className="mt-6 text-center bg-gradient-to-r from-orange-500/20 to-yellow-500/20 border-2 border-orange-400 rounded-xl p-4">
          <p className="text-lg font-semibold text-white">
            🎉 We smashed our {goal_tails}-tail goal! Every donation now adds BONUS tails to feed even more people!
          </p>
          <p className="text-sm text-white/80 mt-2">
            Next milestone: {stretch_goal_tails} tails (${stretch_goal_amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
          </p>
        </div>
      )}

      {/* Initial Goal Reached Message (only in active mode) */}
      {!is_overtime && percent_complete >= 100 && CAMPAIGN_MODE === 'active' && (
        <div className="mt-6 text-center bg-[#DAA520]/20 border-2 border-[#DAA520] rounded-xl p-4">
          <p className="text-lg font-semibold text-white">
            🎉 We've reached our goal! Additional donations will help us serve even more people in need.
          </p>
        </div>
      )}
    </div>
  )
}

export default ProgressBar
